import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import Icon from "@/components/Icon";
import AdminLayout from "@/components/AdminLayout";

export default function AdminEmailPreview() {
  const [tpls, setTpls] = useState([]);
  const [event, setEvent] = useState("welcome");
  const [plan, setPlan] = useState("advanced");
  const [preview, setPreview] = useState({ subject: "", html: "" });
  const [device, setDevice] = useState("desktop");
  const [validation, setValidation] = useState(null);

  useEffect(() => { api.get("/admin/email/templates").then(({ data }) => setTpls(data.templates || [])).catch(() => {}); }, []);
  useEffect(() => {
    setValidation(null);
    api.get("/admin/email/preview", { params: { event, plan } })
      .then(({ data }) => setPreview(data)).catch(() => toast.error("Preview failed"));
  }, [event, plan]);

  const sendTest = async () => {
    try { const { data } = await api.post("/admin/email/test", { event, plan }); toast.success(data.sent ? `Test sent to ${data.to}` : "Email disabled"); }
    catch { toast.error("Send failed"); }
  };
  const validate = async () => {
    try { const { data } = await api.post("/admin/email/validate", { event, plan }); setValidation(data); }
    catch { setValidation({ ok: false, issues: ["validation failed"] }); }
  };

  const cur = tpls.find((t) => t.event === event);

  return (
    <AdminLayout title="Email Preview" subtitle="Preview, test-send and validate transactional emails">
      <div className="grid lg:grid-cols-[300px_1fr] gap-5" data-testid="admin-email-preview">
        <div className="space-y-3">
          <div className="rounded-xl border border-border bg-card p-3.5 space-y-2.5">
            <label className="text-xs font-semibold text-muted-foreground">Template</label>
            <select data-testid="tpl-select" value={event} onChange={(e) => setEvent(e.target.value)} className="w-full rounded-lg border border-input bg-background px-2.5 py-2 text-sm">
              {tpls.map((t) => <option key={t.event} value={t.event}>{t.label}</option>)}
            </select>
            <label className="text-xs font-semibold text-muted-foreground">Sample plan</label>
            <select data-testid="plan-select" value={plan} onChange={(e) => setPlan(e.target.value)} className="w-full rounded-lg border border-input bg-background px-2.5 py-2 text-sm">
              <option value="advanced">Advanced</option>
              <option value="professional">Professional</option>
              <option value="basic">Basic</option>
            </select>
            <div className="text-xs text-muted-foreground pt-1">Variables: <span className="font-mono">{(cur?.variables || []).join(", ") || "—"}</span></div>
          </div>
          <div className="flex gap-2">
            <button data-testid="send-test" onClick={sendTest} className="flex-1 rounded-xl bg-primary text-primary-foreground py-2 text-sm font-bold hover:opacity-90">Send test</button>
            <button data-testid="validate" onClick={validate} className="flex-1 rounded-xl border border-border py-2 text-sm font-semibold hover:bg-accent">Validate</button>
          </div>
          {validation && (
            <div data-testid="validation-result" className={`rounded-xl border p-3 text-sm ${validation.ok ? "border-emerald-500/40 text-emerald-500" : "border-red-500/40 text-red-500"}`}>
              {validation.ok ? "✓ Valid — links & variables OK" : "Issues: " + (validation.issues || []).join("; ")}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="text-sm"><span className="text-muted-foreground">Subject:</span> <span className="font-semibold" data-testid="preview-subject">{preview.subject}</span></div>
            <div className="flex gap-1">
              <button data-testid="device-desktop" onClick={() => setDevice("desktop")} className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${device === "desktop" ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>Desktop</button>
              <button data-testid="device-mobile" onClick={() => setDevice("mobile")} className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${device === "mobile" ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>Mobile</button>
            </div>
          </div>
          <div className="grid place-items-center bg-muted rounded-lg p-3 overflow-auto">
            <iframe data-testid="preview-frame" title="Email preview" srcDoc={preview.html} style={{ width: device === "mobile" ? 390 : "100%", height: 640, border: "none", background: "#fff", borderRadius: 8 }} />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
